/**
 * CustomHubShell — minimal provider stack for partner-branded custom hub routes.
 *
 * Generalized from the RTTR-specific MinelabRttrShell. Mounts a stripped-
 * down provider tree around a single registered campaign's route surface
 * so partner campaigns boot fast and look brand-isolated from frame 1.
 *
 * One shell instance handles one campaign per page lifecycle. The
 * matched campaign (RTTR, Polaris × CoD, ...) is resolved at mount time
 * in main.tsx via `findRegisteredCustomHubSlug(window.location.pathname)`
 * and the corresponding `CustomHubBrandConfig` is passed in as a prop.
 *
 * Navigation away from a partner route requires a hard page reload to
 * re-enter main.tsx and either pick up the full DevourProviders (for
 * non-partner routes) or a different campaign's shell. Partner routes
 * have no outbound internal links by design.
 *
 * **Provider allow-list** (same rationale as MinelabRttrShell):
 *   KEEP  PersistGate          — Redux auth + per-campaign Redux slice hydration
 *   KEEP  QueryClientProvider  — every API call uses React Query
 *   STRIP ThirdwebProvider     — the "ChallengeLoginModal -> useOnAuthThirdweb"
 *                                dependency this cited does not exist:
 *                                `useOnAuthThirdweb` imports no Thirdweb SDK.
 *                                Removed 2026-08-16; see the note below.
 *   STRIP StatsigProvider      — partner routes have no useGate / useExperiment
 *   STRIP WebsocketProvider    — partner routes subscribe to no socket events
 *   STRIP SocialDrawerProvider — partner routes have no friends drawer
 *   STRIP Sentry.withProfiler  — instrumentation only, not functional
 *   STRIP ElectronUpdateOverlay — only renders in Electron, partner routes are WebView
 *
 * If a future campaign needs a stripped provider, do NOT broaden the
 * allow-list for every campaign — add a per-campaign `requiresProviders`
 * extension to `CustomHubBrandConfig` and conditionally mount.
 *
 * Sibling to: DevourProviders.tsx (full provider stack), main.tsx (path-
 * based fork between the two), customHubs/registry.ts (registered slugs).
 */

import { Component, ErrorInfo, PropsWithChildren, ReactElement, Suspense, useRef } from "react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { RouterProvider, createBrowserRouter } from "react-router";
import { persistor } from "./redux";
import { addError } from "@/redux/meta/metaActions";
import { HubPageRouter } from "@/pages/hub/HubPageRouter";
import { attemptReload, isChunkLoadFailure } from "@/utils/chunkLoadRetry";
import { getApiErrorStatus } from "@/utils/getApiErrorStatus";
import type { CustomHubBrandConfig } from "@/customHubs/registry";

/*
 * NO ThirdwebProvider HERE — do not add one back without a consumer.
 *
 * This shell used to mount `ThirdwebProvider` lazily, on the stated
 * grounds that "the sign-up flow uses Thirdweb's wallet hooks" via
 * `ChallengeLoginModal`. That is not true of the code: the modal imports
 * `useOnAuthThirdweb`, which is 157 lines of redux auth actions, Sentry
 * and Overwolf interop and touches no Thirdweb SDK at all. Only the
 * hook's NAME connected them.
 *
 * Every real consumer of `thirdweb/react` — RedeemLootboxButton,
 * TransferNFTsToCompany, WalletDetailsLauncher, GoVipRewards*,
 * RedeemDrawer, ThirdwebReauthenticateModal — is wallet / NFT / lootbox
 * UI, and none of it renders in a campaign tree.
 *
 * The lazy wrapper did not mitigate it either: the provider wrapped
 * `RouterProvider`, i.e. the whole page, behind `Suspense fallback={null}`.
 * So React had to resolve the chunk before painting anything, and every
 * RTTR and Full Throttle visitor waited on ~877KB (brotli) of wallet SDK
 * in front of a blank screen. Lazy-loading a provider that wraps
 * everything defers nothing — it only turns an entry-chunk cost into a
 * blocking one.
 *
 * If a campaign component ever genuinely needs wallet hooks, wrap THAT
 * component, not the router.
 */

interface CustomHubShellProps {
    /**
     * Registered brand config for the campaign matching the current URL.
     * Resolved by main.tsx via `findRegisteredCustomHubSlug(pathname)`
     * before mount.
     */
    brand: CustomHubBrandConfig;
}

/*
 * ShellErrorBoundary — catches any uncaught render error from a stripped-
 * provider dependency that slipped through the audit. Falls back to the
 * campaign's brand-isolated loader rather than React's white-screen-of-
 * death, so partner-facing surfaces stay brand-isolated even on failure.
 *
 * The loader component is provided per-campaign via the brand config
 * (RTTR → MinelabRttrLoadingFallback, future Polaris/CoD → its own
 * loader). The boundary closes over the loader at instance time so a
 * mid-lifecycle config change (which shouldn't happen — shell instance
 * is tied to one campaign per page) wouldn't surprise it.
 */
class ShellErrorBoundary extends Component<
    PropsWithChildren<{ loaderComponent: CustomHubBrandConfig["loaderComponent"] }>,
    { hasError: boolean }
> {
    constructor(props: PropsWithChildren<{ loaderComponent: CustomHubBrandConfig["loaderComponent"] }>) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): { hasError: boolean } {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        /*
         * A stale-deploy chunk-load failure (DEVOUR-WEB-2XQ, esp. iOS WKWebView
         * on /hub/minelab-rttr) surfaces here as a render error. Left alone the
         * boundary would pin the user on the loader fallback forever — the global
         * chunk-load reload handler in main.tsx never fires because this boundary
         * consumed the error before it reached window.onerror. Detect it and force
         * the same fresh-index.html reload the global handler would have done.
         */
        if (isChunkLoadFailure(error)) {
            attemptReload();
            return;
        }
        console.error("CustomHubShell render error — falling back to campaign loader.", error, info);
    }

    render() {
        if (this.state.hasError) {
            const Loader = this.props.loaderComponent;
            return <Loader />;
        }
        return this.props.children;
    }
}

function CustomHubShell({ brand }: CustomHubShellProps): ReactElement {
    const dispatch = useDispatch();

    /*
     * Per-campaign single-route router. Registers `/hub/<slug>` and
     * `/hub/<slug>/*` only — sub-path matching covers signed-handoff
     * query strings and any deeper paths the partner WebView lands on.
     * Built fresh on each shell mount with the matched campaign's slug;
     * mount lifecycle is one-per-campaign-per-page so the router isn't
     * recreated on prop changes.
     */
    const routerRef = useRef<ReturnType<typeof createBrowserRouter> | null>(null);
    if (!routerRef.current) {
        routerRef.current = createBrowserRouter([
            {
                path: `/hub/${brand.slug}`,
                element: <HubPageRouter slugOverride={brand.slug} />,
            },
            {
                path: `/hub/${brand.slug}/*`,
                element: <HubPageRouter slugOverride={brand.slug} />,
            },
        ]);
    }

    /*
     * Mirror DevourProviders' QueryClient configuration so partner queries
     * behave identically to platform queries — same retry rules, same
     * stale-time, same suppressed-error allow-list. Diverging from the
     * platform defaults silently is a source of bugs ("why does this
     * query retry differently on RTTR than elsewhere?").
     *
     * **Canonical source: `web/src/DevourProviders.tsx`.** If the
     * retry/staleTime/refetch policy or the `ignoreErrors` allow-list
     * changes there, propagate the change here. There is no compile-time
     * link between the two — drift produces a silent behavior difference
     * on partner routes only.
     */
    const queryClientRef = useRef<QueryClient | null>(null);
    if (!queryClientRef.current) {
        queryClientRef.current = new QueryClient({
            defaultOptions: {
                queries: {
                    retry: (failureCount, error: any) => {
                        const status = getApiErrorStatus(error);
                        if (status !== undefined && status >= 400 && status < 500) {
                            return false;
                        }
                        return failureCount < 2;
                    },
                    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
                    staleTime: 5 * 60 * 1000,
                    refetchOnWindowFocus: false,
                    refetchOnReconnect: true,
                },
            },
            queryCache: new QueryCache({
                onError: async (error: any) => {
                    const ignoreErrors = [
                        "Connector not connected",
                        "Load failed",
                        "Network request failed",
                        "Failed to fetch",
                        "You are not logged in",
                        "Please log in to continue",
                        "not logged in",
                        "No host found at this slug",
                        "Challenge not found",
                    ];

                    /*
                     * Mirrors DevourProviders: since openapi-generator 7.14.0 the client
                     * throws a ResponseError holding the Response on `.response`, and its
                     * `.message` is the constant "Response returned an error code". Taking
                     * that constant would skip the body parse and leave every `ignoreErrors`
                     * entry unmatchable, so it is treated as "no message".
                     */
                    const RESPONSE_ERROR_CONSTANT = "Response returned an error code";
                    const response: Response | undefined =
                        typeof error?.response?.clone === "function"
                            ? error.response
                            : typeof error?.clone === "function"
                                ? error
                                : undefined;

                    let errorMessage: string | undefined =
                        error?.message === RESPONSE_ERROR_CONSTANT ? undefined : error?.message;
                    if (!errorMessage && response) {
                        try {
                            const body = await response.clone().json();
                            errorMessage = body?.message ?? body?.error;
                        } catch {
                            // Non-JSON response body — leave errorMessage undefined
                        }
                    }

                    const isAuthError = getApiErrorStatus(error) === 400 && (
                        errorMessage?.toLowerCase().includes("log") ||
                        errorMessage?.toLowerCase().includes("auth")
                    );

                    if (error && !isAuthError && !ignoreErrors.some((msg) => errorMessage?.includes(msg))) {
                        dispatch(await addError(error));
                    }
                },
            }),
        });
    }

    return (
        <PersistGate persistor={persistor} loading={null}>
            <QueryClientProvider client={queryClientRef.current}>
                <Suspense fallback={null}>
                    <ShellErrorBoundary loaderComponent={brand.loaderComponent}>
                        <RouterProvider router={routerRef.current} />
                    </ShellErrorBoundary>
                </Suspense>
            </QueryClientProvider>
        </PersistGate>
    );
}

export default CustomHubShell;
